"use client";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import type { School } from "@/schemas/analyzed-property";
import {
  ChevronDown,
  ExternalLink,
  GraduationCap,
  Info,
  MapPin,
  School as SchoolIcon,
  Star,
  Users,
} from "lucide-react";
import Link from "next/link";
import { useState } from "react";

// Interface for property_json_data schools
export interface PropertyJsonSchool {
  link: string;
  name: string;
  size: number | null;
  type: string;
  level: string;
  grades: string;
  rating: number;
  assigned: boolean | null;
  distance: number;
  isAssigned: boolean | null;
  totalCount: number | null;
  studentsPerTeacher: number | null;
}

// Helper function to convert school names to title case
function formatSchoolName(name: string): string {
  // Split on spaces and handle each word
  return name
    .split(" ")
    .map((word) => {
      // Keep acronyms like "STEAM" or "PK" uppercase
      if (word.length <= 3 && word === word.toUpperCase()) {
        return word;
      }
      // Handle hyphenated words
      if (word.includes("-")) {
        return word
          .split("-")
          .map(
            (part) =>
              part.charAt(0).toUpperCase() + part.slice(1).toLowerCase(),
          )
          .join("-");
      }
      // Regular word capitalization
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(" ");
}

interface PropertySchoolsProps {
  schools: School[] | null;
  fallbackSchools?: PropertyJsonSchool[] | null;
  className?: string;
}

function SchoolTypeIcon({ type }: { type: string }) {
  if (type.toLowerCase().includes("charter"))
    return <SchoolIcon className="h-4 w-4" />;
  if (type.toLowerCase().includes("private"))
    return <GraduationCap className="h-4 w-4" />;
  return <SchoolIcon className="h-4 w-4" />;
}

function getSchoolLevelColor(level: string) {
  if (level.toLowerCase().includes("elementary"))
    return "bg-blue-50 text-blue-700 border-blue-200";
  if (level.toLowerCase().includes("middle"))
    return "bg-purple-50 text-purple-700 border-purple-200";
  if (level.toLowerCase().includes("high"))
    return "bg-amber-50 text-amber-700 border-amber-200";
  return "bg-gray-50 text-gray-700 border-gray-200";
}

function getSchoolRatingLevel(rating: number, outOf = 10) {
  const normalizedRating = (rating / outOf) * 10; // Convert to 0-10 scale
  if (normalizedRating >= 8) return "Excellent";
  if (normalizedRating >= 6) return "Good";
  if (normalizedRating >= 4) return "Average";
  return "Below Average";
}

function getRatingColor(rating: number, outOf = 10) {
  const normalizedRating = (rating / outOf) * 10; // Convert to 0-10 scale
  if (normalizedRating >= 8) return "text-emerald-600";
  if (normalizedRating >= 6) return "text-blue-600";
  if (normalizedRating >= 4) return "text-amber-600";
  return "text-red-600";
}

function SchoolCard({
  school,
  isPrimary = false,
}: { school: School | PropertyJsonSchool; isPrimary?: boolean }) {
  const isSchool = "levels" in school;
  const rating = isSchool ? school.rating : school.rating;
  const outOf = isSchool ? 10 : 5;
  const level = isSchool
    ? school.levels.elementary
      ? "Elementary"
      : school.levels.middle
        ? "Middle"
        : "High"
    : school.level;

  return (
    <Card
      className={cn(
        "transition-all duration-200 hover:shadow-md overflow-hidden",
        isPrimary && "border-blue-100",
      )}
    >
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start justify-between">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <SchoolTypeIcon type={school.type} />
              <h3 className="font-semibold">{formatSchoolName(school.name)}</h3>
            </div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <span>{school.type}</span>
              <span>•</span>
              <span>Grades {school.grades}</span>
            </div>
          </div>

          <div
            className={cn(
              "flex items-center gap-1.5 px-2.5 py-1 rounded-full",
              getRatingColor(rating, outOf) === "text-emerald-600" &&
                "bg-emerald-50 border border-emerald-100",
              getRatingColor(rating, outOf) === "text-blue-600" &&
                "bg-blue-50 border border-blue-100",
              getRatingColor(rating, outOf) === "text-amber-600" &&
                "bg-amber-50 border border-amber-100",
              getRatingColor(rating, outOf) === "text-red-600" &&
                "bg-red-50 border border-red-100",
            )}
          >
            <Star
              className={cn("h-3.5 w-3.5", getRatingColor(rating, outOf))}
            />
            <span
              className={cn(
                "font-semibold text-sm",
                getRatingColor(rating, outOf),
              )}
            >
              {rating}/{outOf}
            </span>
          </div>
        </div>

        <div className="flex items-center justify-between">
          <span
            className={cn(
              "px-2.5 py-0.5 text-xs font-medium rounded-full border",
              getSchoolLevelColor(level),
            )}
          >
            {level}
          </span>

          {isSchool ? (
            <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
              <Users className="h-3.5 w-3.5" />
              <span>{school.enrollment.toLocaleString()} students</span>
            </div>
          ) : (
            <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
              <MapPin className="h-3.5 w-3.5" />
              <span>{school.distance} mi</span>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function SchoolSummary({
  schools,
  elementaryRating,
  middleRating,
  highRating,
}: {
  schools: (School | PropertyJsonSchool)[];
  elementaryRating: number;
  middleRating: number;
  highRating: number;
}) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 w-full">
      {elementaryRating > 0 && (
        <div className="flex items-center justify-between sm:flex-col sm:items-center p-4 border rounded-lg">
          <div className="flex items-center gap-3 sm:flex-col sm:text-center">
            <SchoolIcon className="h-5 w-5 text-blue-600 sm:h-8 sm:w-8 sm:mb-2" />
            <h4 className="font-semibold">Elementary</h4>
          </div>
          <div className="flex flex-col items-end sm:items-center sm:mt-2">
            <div className="flex items-center gap-1">
              <Star
                className={cn("h-4 w-4", getRatingColor(elementaryRating))}
              />
              <span
                className={cn(
                  "font-bold text-lg",
                  getRatingColor(elementaryRating),
                )}
              >
                {elementaryRating.toFixed(1)}
              </span>
              <span className="text-xs text-muted-foreground">/10</span>
            </div>
            <span className="text-sm text-muted-foreground">
              {getSchoolRatingLevel(elementaryRating)}
            </span>
          </div>
        </div>
      )}

      {middleRating > 0 && (
        <div className="flex items-center justify-between sm:flex-col sm:items-center p-4 border rounded-lg">
          <div className="flex items-center gap-3 sm:flex-col sm:text-center">
            <SchoolIcon className="h-5 w-5 text-purple-600 sm:h-8 sm:w-8 sm:mb-2" />
            <h4 className="font-semibold">Middle</h4>
          </div>
          <div className="flex flex-col items-end sm:items-center sm:mt-2">
            <div className="flex items-center gap-1">
              <Star className={cn("h-4 w-4", getRatingColor(middleRating))} />
              <span
                className={cn(
                  "font-bold text-lg",
                  getRatingColor(middleRating),
                )}
              >
                {middleRating.toFixed(1)}
              </span>
              <span className="text-xs text-muted-foreground">/10</span>
            </div>
            <span className="text-sm text-muted-foreground">
              {getSchoolRatingLevel(middleRating)}
            </span>
          </div>
        </div>
      )}

      {highRating > 0 && (
        <div className="flex items-center justify-between sm:flex-col sm:items-center p-4 border rounded-lg">
          <div className="flex items-center gap-3 sm:flex-col sm:text-center">
            <GraduationCap className="h-5 w-5 text-amber-600 sm:h-8 sm:w-8 sm:mb-2" />
            <h4 className="font-semibold">High</h4>
          </div>
          <div className="flex flex-col items-end sm:items-center sm:mt-2">
            <div className="flex items-center gap-1">
              <Star className={cn("h-4 w-4", getRatingColor(highRating))} />
              <span
                className={cn("font-bold text-lg", getRatingColor(highRating))}
              >
                {highRating.toFixed(1)}
              </span>
              <span className="text-xs text-muted-foreground">/10</span>
            </div>
            <span className="text-sm text-muted-foreground">
              {getSchoolRatingLevel(highRating)}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

export function PropertySchools({
  schools,
  fallbackSchools,
  className,
}: PropertySchoolsProps) {
  const [showAllSchools, setShowAllSchools] = useState(false);

  if (!schools?.length && !fallbackSchools?.length) return null;

  let displaySchools: (School | PropertyJsonSchool)[] = [];
  let isPrimarySource = false;

  // Use the primary schools data if available
  if (schools?.length) {
    displaySchools = schools;
    isPrimarySource = true;
  } else if (fallbackSchools?.length) {
    displaySchools = fallbackSchools;
    isPrimarySource = false;
  }

  // Calculate average ratings by level
  const elementarySchools = isPrimarySource
    ? displaySchools.filter((s) => (s as School).levels.elementary)
    : displaySchools.filter(
        (s) => (s as PropertyJsonSchool).level === "Primary",
      );

  const middleSchools = isPrimarySource
    ? displaySchools.filter((s) => (s as School).levels.middle)
    : displaySchools.filter(
        (s) => (s as PropertyJsonSchool).level === "Middle",
      );

  const highSchools = isPrimarySource
    ? displaySchools.filter((s) => (s as School).levels.high)
    : displaySchools.filter((s) => (s as PropertyJsonSchool).level === "High");

  const elementaryRating =
    elementarySchools.length > 0
      ? elementarySchools.reduce((sum, school) => sum + school.rating, 0) /
        elementarySchools.length
      : 0;

  const middleRating =
    middleSchools.length > 0
      ? middleSchools.reduce((sum, school) => sum + school.rating, 0) /
        middleSchools.length
      : 0;

  const highRating =
    highSchools.length > 0
      ? highSchools.reduce((sum, school) => sum + school.rating, 0) /
        highSchools.length
      : 0;

  // Sort schools by rating (highest first)
  const sortedSchools = [...displaySchools].sort((a, b) => b.rating - a.rating);

  // Get top schools for display (show at most 5)
  const topSchools = sortedSchools.slice(0, 5);
  const hasMoreSchools = sortedSchools.length > 5;

  return (
    <Card className={cn("overflow-hidden", className)}>
      <CardContent className="p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <h3 className="text-lg font-semibold">Schools</h3>
            <p className="text-sm text-muted-foreground">
              {elementaryRating > 0 || middleRating > 0 || highRating > 0
                ? "Nearby school quality by education level"
                : "Details about nearby schools in this area"}
            </p>
          </div>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger>
                <div className="flex items-center gap-1.5 text-muted-foreground">
                  <Info className="h-4 w-4" />
                  <span className="text-sm">About Ratings</span>
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <p className="max-w-xs">
                  {isPrimarySource
                    ? "School ratings (1-10) are based on test scores, college readiness, and equity. Parent ratings (1-5) reflect community feedback."
                    : "School ratings (1-5) are provided by GreatSchools and based on test scores, equity, and college readiness factors."}
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>

        {/* School Rating Summary */}
        {(elementaryRating > 0 || middleRating > 0 || highRating > 0) && (
          <SchoolSummary
            schools={displaySchools}
            elementaryRating={elementaryRating}
            middleRating={middleRating}
            highRating={highRating}
          />
        )}

        {/* Show/Hide Schools Button */}
        <Button
          variant="outline"
          className="w-full"
          onClick={() => setShowAllSchools(!showAllSchools)}
        >
          {showAllSchools ? "Hide School Details" : "View Top Schools"}
          <ChevronDown
            className={cn(
              "ml-2 h-4 w-4 transition-transform",
              showAllSchools && "rotate-180",
            )}
          />
        </Button>

        {/* School List - Only shown when expanded */}
        {showAllSchools && (
          <div className="space-y-4">
            {topSchools.map((school, index) => (
              <SchoolCard
                key={school.name}
                school={school}
                isPrimary={index === 0}
              />
            ))}

            {hasMoreSchools && (
              <div className="text-center pt-2">
                <span className="text-sm text-muted-foreground">
                  Showing top {topSchools.length} of {sortedSchools.length}{" "}
                  schools
                </span>
              </div>
            )}

            {/* Link to GreatSchools */}
            <Link
              href="https://www.greatschools.org/search/search.page"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex w-full items-center justify-center gap-2 rounded-lg border border-gray-200 bg-white px-4 py-2.5 text-sm font-semibold text-gray-700 transition-all hover:border-gray-300 hover:bg-gray-50 hover:text-gray-900 hover:shadow-sm"
            >
              <ExternalLink className="h-4 w-4 text-gray-500" />
              See more schools on GreatSchools
            </Link>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
